import {Icon, Popover, Tag, Tooltip} from "@blueprintjs/core";

import styled from "@emotion/styled";
import {debounce} from "lodash";
import {useEffect, useMemo, useState} from "react";
import {StyledButton} from "./IndicatorSelect";

import InfiniteScroll from "react-infinite-scroll-component";
import {OptionType} from "./types/option";
import {DEFAULT_POPOVER_MODIFIERS} from "../../constants/constants";

type OptionItemProps = {
  item: OptionType;
  handleClick: (item: OptionType) => void;
  tooltip?: string | JSX.Element;
  label?: string;
};

type SelectedItemProps = {
  item: OptionType;
  handleClick: (item: OptionType) => void;
  label?: string;
};
interface MultiSelectProps<T> {
  options: T[];
  testIdPrefix?: string;
  onOptionSelect: (item: any) => void;
  selectedOptions: T[];
  onClear: () => void;
  placeholder: string;
  text?: string;
  onQueryChange: (query: string) => void;
  onLoadMore?: () => void;
  activeBackGroundColor?: string;
  defaultTextColor?: string;
  iconSize?: number;
  maxHeight?: number;
  maxWith?: number;
  exceedMaxSelection?: number;
  maxHeightOfOptionOrSelection?: number;
  hideSearch?: boolean;
  loading?: boolean;
  customButton?: React.ReactNode;
  /** DP-3115: opt-in map of option key → tooltip CONTENT. When set, the option label is wrapped in a
   *  Blueprint Tooltip (no info icon) so hovering the row shows it. Absent key = plain label (other selects unaffected). */
  optionTooltips?: Record<string | number, string | JSX.Element>;
  /** DP-3115: opt-in map of option key → DISPLAY-ONLY label override (option row + selected chip).
   *  Does NOT change the stored/selected value — selection still uses the real `options` entries. */
  optionLabels?: Record<string | number, string>;
}

const FilterableMultiSelect = (props: MultiSelectProps<OptionType>) => {
  return (
    <StyledPopover
      popoverClassName="colorized-indicators-multiselect-popover"
      data-testid={`${props.testIdPrefix}-filterable-multiselect-popover`}
      content={<MultipleSelect {...props} />}
      position="bottom-left"
      minimal={true}
      modifiers={DEFAULT_POPOVER_MODIFIERS}
    >
      {props.customButton ? (
        props.customButton
      ) : (
        <StyledButton
          maxWidth={props.maxWith}
          hasValue={Boolean(props.selectedOptions?.length > 0)}
          data-testid={`${props.testIdPrefix}-filterable-multiselect`}
          text={props.text}
          activeBackGroundColor={props.activeBackGroundColor ?? "#8DA4BE"}
          defaultTextColor={props.defaultTextColor ?? "#8DA4BE"}
          endIcon={<Icon className="dropdown-icon" icon="chevron-down" size={props.iconSize ?? undefined} />}
          loading={props.loading}
          disabled={props.loading}
        />
      )}
    </StyledPopover>
  );
};

export default FilterableMultiSelect;

const isClearDisabled = (selectedOptions: OptionType[]) => {
  return !selectedOptions || selectedOptions.length === 0;
};
const defaultMaxHeightOfOptionList = 200;
const defaultMaxHeightOfSelectionList = 160;
const MultipleSelect = ({
  options,
  testIdPrefix = "",
  onClear,
  onOptionSelect,
  placeholder,
  selectedOptions,
  onQueryChange,
  onLoadMore = () => {
    /* NO OP */
  },
  maxHeight,
  maxWith,
  exceedMaxSelection,
  maxHeightOfOptionOrSelection,
  hideSearch = false,
  optionTooltips,
  optionLabels
}: MultiSelectProps<OptionType>) => {
  const [unselectedOptions, setUnselectedOptions] = useState<OptionType[]>([]);
  const selectedOptionKeys = useMemo<any[]>(() => selectedOptions.map((o) => o.key), [selectedOptions]);
  useEffect(() => {
    return () => {
      onQueryChange("");
    };
  }, [onQueryChange]);

  useEffect(() => {
    const unselected = options.filter((o) => !selectedOptionKeys.includes(o.key));
    setUnselectedOptions(unselected);
  }, [options, selectedOptionKeys]);

  const onChangeFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    onQueryChange(query);
  };

  const renderOptionList = (options: OptionType[]) => {
    return options.map((option) => {
      return (
        <OptionItem
          key={option.key}
          item={option}
          handleClick={onOptionSelect}
          tooltip={optionTooltips?.[option.key]}
          label={optionLabels?.[option.key]}
        />
      );
    });
  };

  const renderTagList = (options: OptionType[]) => {
    if (!options || options.length === 0) {
      return [] as Element[];
    } else {
      return options.map((option) => {
        return (
          <ItemTag
            data-testid={`selected-item-${option.key}`}
            key={option.key}
            item={option}
            handleClick={onOptionSelect}
            label={optionLabels?.[option.key]}
          />
        );
      });
    }
  };
  const renderCount = () => {
    return `${selectedOptions.length} of ${exceedMaxSelection}`;
  };
  const renderMaxHeightSelectionList = useMemo(() => {
    return maxHeightOfOptionOrSelection ?? defaultMaxHeightOfSelectionList;
  }, [maxHeightOfOptionOrSelection]);
  const renderMaxHeightOptionList = useMemo(() => {
    return maxHeightOfOptionOrSelection ?? defaultMaxHeightOfOptionList;
  }, [maxHeightOfOptionOrSelection]);
  return (
    <Container data-testid={`${testIdPrefix}-multiple-select`} maxWidth={maxWith}>
      <Body>
        {!hideSearch && (
          <SearchContainer>
            <Search>
              <StyledIcon icon="search" />
              <StyledInput
                data-testid={`${testIdPrefix}-multi-search-input`}
                type="text"
                placeholder={placeholder}
                onChange={debounce(onChangeFilter, 400)}
              />
            </Search>
          </SearchContainer>
        )}
        {selectedOptions.length > 0 && (
          <SelectedItemsContainer>
            <SelectedItemsList
              maxHeight={maxHeight && unselectedOptions.length === 0 ? maxHeight : renderMaxHeightSelectionList}
            >
              {renderTagList(selectedOptions)}
            </SelectedItemsList>
          </SelectedItemsContainer>
        )}
        {unselectedOptions.length > 0 && (
          <OptionsContainer>
            <InfiniteScroll
              dataLength={unselectedOptions.length}
              next={onLoadMore}
              hasMore={true}
              loader={""}
              scrollableTarget="infinity-scroll-list"
            >
              <OptionList
                id="infinity-scroll-list"
                data-testid={`${testIdPrefix}-infinity-scroll-list`}
                maxHeight={maxHeight && selectedOptions.length === 0 ? maxHeight : renderMaxHeightOptionList}
              >
                {renderOptionList(unselectedOptions)}
              </OptionList>
            </InfiniteScroll>
          </OptionsContainer>
        )}
      </Body>
      <Footer>
        {exceedMaxSelection && (
          <SelectionCounter isExceed={exceedMaxSelection === selectedOptions.length}>{renderCount()}</SelectionCounter>
        )}
        <ClearButton onClick={onClear} disabled={isClearDisabled(selectedOptions)}>
          Clear Filter <span style={{color: "red"}}>({selectedOptions?.length ?? 0})</span>
        </ClearButton>
      </Footer>
    </Container>
  );
};

const ItemTag = ({item, handleClick, label}: SelectedItemProps) => {
  return (
    <StyledTag
      style={{margin: "1px"}}
      key={item.key}
      endIcon={
        <Icon
          data-testid={`item-deselect-${item.key}`}
          style={{cursor: "pointer"}}
          icon="cross"
          size={16}
          onClick={() => handleClick(item)}
        />
      }
    >
      {label ?? item.value}
    </StyledTag>
  );
};

// DP-3115: when an option carries a tooltip, the OPTION ROW ONLY shows the quoted label
// wrapped in a Blueprint Tooltip (no info icon). The selected chip (ItemTag) stays bare.
const OptionItem = ({item, handleClick, tooltip, label}: OptionItemProps) => {
  // `label` is a DISPLAY-ONLY override (e.g. the quoted "Both") — the stored value is unchanged.
  const displayValue = label ?? item.value;
  return (
    <Option data-testid="option-item" key={item.key} onClick={() => handleClick(item)}>
      {tooltip ? (
        // `fill` makes the tooltip target span the full row width so hovering anywhere on the
        // row (not just the short label) shows it; placement="right" opens it to the side.
        <Tooltip content={tooltip} placement="right" popoverClassName="content-tooltip" fill>
          <span>{displayValue}</span>
        </Tooltip>
      ) : (
        displayValue
      )}
    </Option>
  );
};

const StyledPopover = styled(Popover)``;

const Container = styled.div<{maxWidth?: number}>`
  font-family: "Roboto", sans-serif;
  width: ${(props) => (props.maxWidth ? `${props.maxWidth}px` : "max-content")};
  max-width: 300px;
  background-color: white;
  box-shadow: 0px 2px 6px 0px #0000001a;
  border-radius: 4px;
`;

const SearchContainer = styled.div`
  padding: 8px 10px;
  box-shadow: 0px 1px 2px 0px #0000001a;
  background-color: #fcfcfc;
  height: 48px;
`;

const SelectedItemsContainer = styled.div`
  padding: 3px;
  box-shadow: 0px 1px 2px 0px #0000001a;
`;

const SelectedItemsList = styled.div<{maxHeight: number}>`
  overflow-y: auto;
  padding: 6px 10px;
  background-color: #fcfcfc;
  max-height: ${(props) => `${props.maxHeight}px`};
`;

const OptionsContainer = styled.div`
  padding: 3px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
`;

const Footer = styled.div`
  padding: 10px;
  background-color: #fcfcfc;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  border-radius: 4px;
  box-shadow: 0px 0px 3px 0px #00000026;
  height: 36px;
`;

const OptionList = styled.ul<{maxHeight: number}>`
  overflow-y: auto;
  list-style: none;
  text-overflow: ellipsis;
  padding: 0;
  margin: 0;
  max-height: ${(props) => `${props.maxHeight}px`};
`;

const StyledTag = styled(Tag)`
  background-color: #8da4beb2;
  font-size: 12px;
  border-radius: 4px;
  padding: 2px 6px;

  .bp5-text-overflow-ellipsis {
    white-space: pre-wrap;
  }
`;

const Option = styled.li`
  border-radius: 4px;
  padding: 5px;
  margin: 5px;
  color: #161616;
  font-size: 14px;
  white-space: pre-wrap;

  :hover {
    background-color: #0038ff1a;
    cursor: pointer;
  }
`;

const Search = styled.div`
  font-family: "Roboto", sans-serif;
  position: relative;
  border: 1px solid #c3c3c373;
  border-radius: 4px;
  display: flex;
  height: 32px;
  align-items: center;
`;

const StyledIcon = styled(Icon)`
  color: #a4a5a6;
  padding: 8px 8px 8px 12px;
`;

const StyledInput = styled.input`
  display: inline;
  width: 100%;
  line-height: 16px;
  border: none;
  outline: none;
`;

const ClearButton = styled.button`
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
  border: none;
  color: #14305a;
  background-color: transparent;
  cursor: pointer;

  span {
    color: #fa545e;
  }

  :disabled {
    opacity: 0.5;
  }
`;
const SelectionCounter = styled.span<{isExceed: boolean}>`
  font-size: 10px;
  font-weight: 400;
  color: ${(props) => (props.isExceed ? "#FA545E" : "#797979")};
  margin: 0 auto 0 3px;
`;
